import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';

// imports for subscription
import { split, ApolloLink, Observable } from 'apollo-link';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';


const uriHttp = process.env.VUE_APP_SERVER_URL;
const uriWs = process.env.VUE_APP_WS_SERVER_URL;


const headers = { authorization: localStorage.getItem('token') };

const httpLink = new HttpLink({ uri: uriHttp, headers });


const wsLink = new WebSocketLink({
  uri: uriWs,
  options: {
    reconnect: true,
    // lazy: true,
    connectionParams() {
      return { headers };
    },
  },
});


wsLink.subscriptionClient.maxConnectTimeGenerator.duration = () => wsLink.subscriptionClient.maxConnectTimeGenerator.max;

// if private gate is true then we get wsLink else httpLink
const httpOrWsLink = split(({ query }) => {
  const { kind, operation } = getMainDefinition(query);
  return kind === 'OperationDefinition' && operation === 'subscription';
}, wsLink, httpLink);

// Target company and helper to decide gating per operation
const TARGET_COMPANY_ID = 'cl44pw1bsbvmk0848ef4sci1r';
const BANKSY_LA_TEAM_IDS = ['cl6pcnnig1e960882s62wposu', 'cl5jrzickryjn0881uofzuqo6'];

function isTargetForMock(opName, variables = {}) {
  // Common patterns: direct companyId or id as company id
  if (variables.companyId && variables.companyId === TARGET_COMPANY_ID) return true;
  if (variables.id && variables.id === TARGET_COMPANY_ID) return true;

  // GET_BUDGET_ALLOCATED_GQL doesn't carry companyId; gate by known Banksy LA team ids
  if (opName === 'GET_BUDGET_ALLOCATED_GQL' && variables.groupId) {
    return BANKSY_LA_TEAM_IDS.includes(variables.groupId);
  }

  return false;
}

// Demo mock link (always enabled, but gated per company/op)
const mockLink = new ApolloLink((operation, forward) => {
  const opName = operation.operationName;
  const vars = operation.variables || {};

  // Mock: Budget totals for company (WidgetBudget)
  if (opName === 'GET_COMPANY_CALCULATION_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    return Observable.of({
      data: {
        companiesTotalCredit: {
          available: 250000,
          expense: 75000,
          allocated: 150000,
          compneyBudget: 300000,
          teamsBudgets: 120000,
          actuleUpperLimit: 350000,
          __typename: 'CompaniesTotalCredit',
        },
      },
    });
  }

  // Mock: Top Merchant Average Increase Data
  if (opName === 'GET_TOP_MERCHANT_AVERAGE_INCREASE_DATA_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { filterOffSet = 0 } = vars;
    const page0 = [
      { name: 'Amazon', fistFilterValue: 12000, secondFilterValue: 15000, diff: 3000, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Google Cloud', fistFilterValue: 9000, secondFilterValue: 12000, diff: 3000, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Uber', fistFilterValue: 5000, secondFilterValue: 7000, diff: 2000, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Apple', fistFilterValue: 8000, secondFilterValue: 9500, diff: 1500, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Slack', fistFilterValue: 3000, secondFilterValue: 4000, diff: 1000, __typename: 'TopMerchantAverageIncreaseData' },
    ];
    const page1 = [
      { name: 'Microsoft', fistFilterValue: 11000, secondFilterValue: 14000, diff: 3000, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Zoom', fistFilterValue: 7000, secondFilterValue: 9500, diff: 2500, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Airbnb', fistFilterValue: 4500, secondFilterValue: 6200, diff: 1700, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Lyft', fistFilterValue: 3800, secondFilterValue: 5200, diff: 1400, __typename: 'TopMerchantAverageIncreaseData' },
      { name: 'Notion', fistFilterValue: 2600, secondFilterValue: 3600, diff: 1000, __typename: 'TopMerchantAverageIncreaseData' },
    ];

    return Observable.of({
      data: {
        getTopMerchantAverageIncreaseData: filterOffSet === 1 ? page1 : page0,
      },
    });
  }

  // Mock: Team allocated budget by group (split evenly across two teams from monthly totals)
  if (opName === 'GET_BUDGET_ALLOCATED_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { groupId, month } = vars;

    // Keep these in sync with GET_ALL_TEAM_BUDGET_MONTHLY_GQL mock
    const currentMonthTotals = { totalBudget: 300000, totalAllocated: 180000 };
    const lastMonthTotals = { totalBudget: 295000, totalAllocated: 175000 };
    const totals = month === 7 ? lastMonthTotals : currentMonthTotals;

    // Two team IDs from the HAR allTeam array
    const teamIds = ['cl6pcnnig1e960882s62wposu', 'cl5jrzickryjn0881uofzuqo6'];
    const perTeamAllocated = Math.round(totals.totalAllocated / teamIds.length);

    // Simple category split summing to perTeamAllocated
    const catSplits = [
      { name: 'Software', amount: Math.round(perTeamAllocated * 0.45) },
      { name: 'Travel', amount: Math.round(perTeamAllocated * 0.35) },
      { name: 'Marketing', amount: perTeamAllocated - Math.round(perTeamAllocated * 0.45) - Math.round(perTeamAllocated * 0.35) },
    ];

    const groupNameMap = {
      cl6pcnnig1e960882s62wposu: 'Banksy LA - Team A',
      cl5jrzickryjn0881uofzuqo6: 'Banksy LA - Team B',
    };

    const categories = catSplits.map((c, idx) => ({
      id: `${groupId}-cat-${idx + 1}`,
      categoryName: c.name,
      categorybudgets: [
        {
          id: `${groupId}-catbud-${idx + 1}`,
          amount: c.amount,
          __typename: 'CategoryBudget',
        },
      ],
      __typename: 'Category',
    }));

    return Observable.of({
      data: {
        groups: [
          {
            id: groupId,
            groupName: groupNameMap[groupId] || 'Banksy LA',
            groupbudgets: [
              { id: `${groupId}-gb-1`, amount: perTeamAllocated, __typename: 'GroupBudget' },
            ],
            categories,
            __typename: 'Group',
          },
        ],
      },
    });
  }

  // Mock: Top Category Spending Insights
  if (opName === 'GET_TOP_CATEGORY_SPENDING_INSIGHTS_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { filterOffSet = 0 } = vars;
    const page0 = [
      { categoryName: 'Software', teamName: 'Engineering', spentAmount: 24500, percentage: 34, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Travel', teamName: 'Sales', spentAmount: 16800, percentage: 23, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Marketing', teamName: 'Growth', spentAmount: 12900, percentage: 18, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Office Supplies', teamName: 'Operations', spentAmount: 7600, percentage: 11, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Meals', teamName: 'All Teams', spentAmount: 5400, percentage: 8, __typename: 'TopCategorySpendingInsight' },
    ];
    const page1 = [
      { categoryName: 'Cloud Services', teamName: 'Engineering', spentAmount: 23200, percentage: 32, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Events', teamName: 'Marketing', spentAmount: 17500, percentage: 24, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Transportation', teamName: 'Field Ops', spentAmount: 11800, percentage: 16, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Training', teamName: 'HR', spentAmount: 8200, percentage: 12, __typename: 'TopCategorySpendingInsight' },
      { categoryName: 'Subscriptions', teamName: 'All Teams', spentAmount: 6100, percentage: 9, __typename: 'TopCategorySpendingInsight' },
    ];

    return Observable.of({
      data: {
        getTopCategorySpendingInsights: filterOffSet === 1 ? page1 : page0,
      },
    });
  }

  // Mock: Top Merchant Spending Insights
  if (opName === 'GET_TOP_MERCHANT_SPENDING_INSIGHTS_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { filterOffSet = 0 } = vars;
    const page0 = [
      { merchantName: 'Amazon', spentAmount: 22100, percentage: 28, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Google Cloud', spentAmount: 17600, percentage: 22, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Apple', spentAmount: 13400, percentage: 17, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Uber', spentAmount: 9800, percentage: 12, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Slack', spentAmount: 6200, percentage: 8, __typename: 'TopMerchantSpendingInsight' },
    ];
    const page1 = [
      { merchantName: 'Microsoft', spentAmount: 20800, percentage: 27, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Zoom', spentAmount: 16900, percentage: 22, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Airbnb', spentAmount: 12350, percentage: 16, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Lyft', spentAmount: 9100, percentage: 12, __typename: 'TopMerchantSpendingInsight' },
      { merchantName: 'Notion', spentAmount: 5800, percentage: 8, __typename: 'TopMerchantSpendingInsight' },
    ];

    return Observable.of({
      data: {
        getTopMerchantSpendingInsights: filterOffSet === 1 ? page1 : page0,
      },
    });
  }

  // Mock: Top Employee Spending Insights
  if (opName === 'GET_TOP_EMPLOYEE_SPENDING_INSIGHTS_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { filterOffSet = 0 } = vars;
    const page0 = [
      { firstName: 'Alice', lastName: 'Johnson', spentAmount: 18500, percentage: 32, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Bob', lastName: 'Smith', spentAmount: 14200, percentage: 25, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Carol', lastName: 'Nguyen', spentAmount: 9800, percentage: 17, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'David', lastName: 'Lee', spentAmount: 7600, percentage: 13, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Eve', lastName: 'Martinez', spentAmount: 5400, percentage: 9, __typename: 'TopEmployeeSpendingInsight' },
    ];
    const page1 = [
      { firstName: 'Frank', lastName: 'Harris', spentAmount: 17200, percentage: 30, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Grace', lastName: 'Kim', spentAmount: 15050, percentage: 26, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Hank', lastName: 'Patel', spentAmount: 11200, percentage: 20, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Ivy', lastName: 'Brown', spentAmount: 8300, percentage: 14, __typename: 'TopEmployeeSpendingInsight' },
      { firstName: 'Jack', lastName: 'Wilson', spentAmount: 5100, percentage: 10, __typename: 'TopEmployeeSpendingInsight' },
    ];

    return Observable.of({
      data: {
        getTopEmployeeSpendingInsights: filterOffSet === 1 ? page1 : page0,
      },
    });
  }

  // Mock: Company credit limit summary (WidgetBudget)
  if (opName === 'GET_USER_DEFINE_BUDGET') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    return Observable.of({
      data: {
        getCompanyCreditLimit: {
          allocated: 150000,
          expense: 75000,
          available: 250000,
          __typename: 'CompanyCreditLimit',
        },
      },
    });
  }

  // Mock: All Teams monthly budget totals (WidgetBudget "All Teams" view)
  if (opName === 'GET_ALL_TEAM_BUDGET_MONTHLY_GQL') {
    if (!isTargetForMock(opName, vars)) return forward(operation);
    const { month } = vars;
    // Provide slightly different demo numbers for different months
    const currentMonthTotals = {
      totalBudget: 300000,
      totalAllocated: 180000,
      totalSpent: 90000,
      totalUncatorized: 15000,
      __typename: 'AllTeamDashboardMonthWise',
    };
    const lastMonthTotals = {
      totalBudget: 295000,
      totalAllocated: 175000,
      totalSpent: 82000,
      totalUncatorized: 12000,
      __typename: 'AllTeamDashboardMonthWise',
    };

    return Observable.of({
      data: {
        getAllTeamDashboardMonthWise: month === 7 ? lastMonthTotals : currentMonthTotals,
      },
    });
  }

  // Pass-through for all other operations
  return forward(operation);
});

// Compose links: mock first, then actual transport (ws/http)
const link = ApolloLink.from([mockLink, httpOrWsLink]);

export const defaultClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: true,
});

const apolloProvider = new VueApollo({
  defaultClient,
  defaultOptions: {
    $loadingKey: 'loading',
  },
});

Vue.use(VueApollo);

export default apolloProvider;
